import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Card,
    Button,
    TextField,
    Select,
    MenuItem,
    Typography,
    Divider,
    CircularProgress
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import CategoryIcon from '@mui/icons-material/Category';
import TagIcon from '@mui/icons-material/Tag';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { ListOrder, FieldLabel } from "../components";
import {StateContext} from "../../../components/context/StateProvider";
import SalesService from "../../../services/sales.service";
import ProductFactory from "../../../models/ProductFactory";
import Order, {OrderType, orderTypesWithLabelsSpecial} from "../../../models/Order";
import OrderService from "../../../services/order.service";
import Utils from "../../../utils/Utils";
import Toasts from "../../../components/notificacion/Toasts";
import SocketClient from "../../../socket/SocketClient";
import {INCIDENCIA_ORIGEN, ORDER_ACTION} from "../../../config/constants";
import {debounce} from "@mui/material";
import NotesService from "../../../services/notes.service";
import StickerService from "../../../services/sticker.service";
import DeliveryService from "../../../services/delivery.service";
import { ALTURA_BOTON, BOTON_PRIMARIO_SX } from "../../../components/ui/buttonStyles";


const Sales = () => {

    const {state, updateState} = useContext(StateContext);

    // orderType es el tipo de documento que se está buscando (albarán o pedido)
    const [orderType, setOrderType] = useState(OrderType.Albaran.valueOf());

    const orderRef = useRef(null);

    // El callback del socket y el intervalo de nave 2 viven desde el montaje, así que
    // sus closures congelarían el estado y el tipo de aquel momento. Estas refs replican
    // lo que hacía this.context / this.state en la clase: leer siempre el valor actual.
    // Importa sobre todo para acumular toasts sin perder los anteriores y para que una
    // recarga por socket use el tipo que el usuario tiene seleccionado ahora.
    const estadoRef = useRef(state);
    estadoRef.current = state;

    const orderTypeRef = useRef(orderType);
    orderTypeRef.current = orderType;

    // Debounce con instancia única: limpia espacios del número escaneado
    const handleOrderChange = useMemo(() => debounce((event) => {
        if (orderRef.current) {
            orderRef.current.value = event.target.value.replace(/\s/g, "");
        }
    }, 300), []);

    const handleChange = (event) => {
        updateState({order: new Order()});
        updateState({products:[]});
        setOrderType(event.target.value);
    };

    /**
     * Gestionar evento submit
     * @param event
     * @returns {Promise<void>}
     */
    const handleSubmit = async (event) => {

        if (event) {
            event.preventDefault();
        }

        if (orderRef.current.value !== "" && Utils.checkOrder(orderRef.current.value)) {
            updateState({loading: true});
            await getSales(updateState, estadoRef.current, false);
        } else if (orderRef.current.value !== "" && !Utils.checkOrder(orderRef.current.value)) {
            const toasts = Toasts.generateToast('Formato de albarán inválido: SERIE-ALBARAN', 'error');
            updateState({toasts: [...estadoRef.current.toasts, toasts]})
        }
    };

    /**
     * Obtener ventas en funcion de la orden
     * @param updateState
     * @param state
     * @returns {Promise<void>}
     */
    const getSales = async (updateState, state, updateBySocket) => {
        await SalesService.getSales(orderRef.current.value, orderTypeRef.current, state.year, true).then(
            async (data) => {

                try {
                    const stateContext = estadoRef.current;

                    let complete = 0;
                    let lastUpdated = null;

                    const salesMap = data.map(async (sale, index) => {

                        let state = 0;
                        let remaining = sale.UNIDADES;
                        let register = 0;
                        let registerDate = undefined;
                        let manual = false;

                        if (!Utils.checkRefProduct(sale.ARTICULO)) {

                            let refArticulo = sale.ARTICULO;

                            if (sale.DUPLICADO) {
                                refArticulo += '-' + sale.LINIA;
                            }

                            // Obtener ventas
                            const saleRegistry = await SalesService.getSalesProduct(sale.LETRA+'-'+sale.NUMERO, refArticulo, stateContext.year, 1, orderTypeRef.current);

                            // Comprobar ventas quedan quedan por añadir
                            if (saleRegistry.length > 0) {
                                remaining = sale.UNIDADES - saleRegistry[0].unidades;
                                register = saleRegistry[0].unidades;

                                // Cambiar el estado en funcion de los bultos
                                if (remaining === 0) {
                                    state = 1;
                                    complete += 1;
                                }

                                registerDate = saleRegistry[0].registrado;
                                manual = saleRegistry[0].manual;

                                if (lastUpdated === null || lastUpdated < new Date(saleRegistry[0].registrado)) {
                                    lastUpdated = new Date(saleRegistry[0].registrado);
                                }
                            }

                        } else {
                            sale.UNIDADES = 0;
                            remaining = 0;
                            state = 1;
                            complete += 1;
                        }

                        let barrasModels = 'BarrasModels';
                        if (stateContext.year !== Utils.getCurrentYear()) {
                            barrasModels = 'BarrasModel'+stateContext.year+'s';
                        }

                        let product = ProductFactory.productOrder(sale.LETRA+'-'+sale.NUMERO, sale.ARTICULO, sale.INFO_ARTICULO[barrasModels], sale.INFO_ARTICULO.NOMBRE,
                            sale.UNIDADES, sale.INFO_ARTICULO.UBICACION, sale.INFO_ARTICULO.VENSERIE, remaining, state, register, sale.DUPLICADO, sale.LINIA, registerDate, manual, index);
                        product.stock = sale.INFO_ARTICULO?.Stocks2Models;
                        return product;
                    });

                    const salesMapFull = await Promise.all(salesMap);

                    await createOrder(lastUpdated, complete === data.length);

                    let valueFilter = '0';
                    if (complete === data.length) {
                        valueFilter = '1';
                    }

                    if (!updateBySocket) {
                        updateState({filters: [{field: 'state', operator: 'equals', value: valueFilter}]})
                    } else if(complete === data.length){
                        updateState({filters: [{field: 'state', operator: 'equals', value: valueFilter}]})
                    }

                    updateState({products: salesMapFull})
                    updateState({loading: false});

                } catch (error) {
                    const toasts = Toasts.generateToast(error.response?.data?.message || 'Error al obtener el pedido', 'error');
                    updateState({toasts: [...estadoRef.current.toasts, toasts]})
                    updateState({loading: false});
                }
            }
        ).catch((error) => {
            const toasts = Toasts.generateToast(error.response?.data?.message || 'Error al obtener el pedido', 'error');
            updateState({toasts: [...state.toasts, toasts]})
            updateState({loading: false});
        });
    };

    /**
     * Generar pedido
     * @param lastUpdated
     * @param complete
     * @returns {Promise<void>}
     */
    const createOrder = async (lastUpdated, complete) => {

        const stateContext = estadoRef.current;

        let order = new Order();

        order.num = orderRef.current.value;

        if (lastUpdated) {
            order.setLastUpdated(lastUpdated);
        }

        // Obtener informacion ruta
        await getDeliveryOrder(order, stateContext.year);

        await getNoteOrder(order, stateContext.year);

        // Obtener personas asignadas al pedido
        await getPersonsOrder(order, stateContext.year);

        if (order.state === 1) {
            order.complete = true;
        } else {
            order.complete = complete;
        }

        // Comprobar si existe productos en nave 2
        await checkOrderN2(order);

        // Limpiar packages del pedido anterior para forzar recarga en DetallesRuta
        order.packages = null;
        order.lastRegisterPackage = null;

        updateState({order: order});
    };

    /**
     * Obtener información ruta
     * @param order
     * @param year
     * @returns {Promise<void>}
     */
    const getDeliveryOrder = async (order, year) => {
        let infoOrder = Utils.getOrder(order.num);
        let sticker = await StickerService.getStickerMap(infoOrder.serie, infoOrder.order, year);
        if (sticker) {
            let delivery = await DeliveryService.getDelivery(sticker.delivery);
            order.delivery = delivery;
            order.sticker = sticker;
        }
    };

    /**
     * Obtener información incidencias
     * @param order
     * @param year
     * @returns {Promise<void>}
     */
    const getNoteOrder = async (order, year) => {
        let infoOrder = Utils.getOrder(order.num);

        const currentNote = await NotesService.getNoteByNum(infoOrder.serie, infoOrder.order, INCIDENCIA_ORIGEN.SALES);

        if (currentNote && Object.keys(currentNote).length > 0) {
            order.Note = currentNote;
        }
    };

    /**
     * Obtener personas asignadas al pedido
     * @param order
     * @returns {Promise<void>}
     */
    const getPersonsOrder = async (order, year) => {
        const persons = await OrderService.getPersonsOrder(orderRef.current.value, 'ventas', year, orderTypeRef.current);
        if (persons.length > 0) {
            order.reviewer = (persons[0].revisado === null ? '0' : persons[0].revisado);
            order.preparer = (persons[0].preparado === null ? '0' : persons[0].preparado);
            order.camera = persons[0].camara;
            order.state = persons[0].estado;
            order.location = persons[0].localizacion;
            order.pedido = persons[0].numero_pedido;
            if (persons[0].imagenes) {
                order.imagenes = persons[0].imagenes;
            }
        }
    };

    /**
     * Comprobar si tiene productos en nave 2
     * @param order
     * @returns {Promise<void>}
     */
    const checkOrderN2 = async (order) => {

        const stateContext = estadoRef.current;

        let update = false;

        if (order === null) {
            order = stateContext.order;
            update = true;
        }

        if (order.num !== null) {

            const response = await OrderService.checkOrderN2(order.num, stateContext.year, orderTypeRef.current);

            if (response?.details?.existen) {

                const orderState = await OrderService.getStateOrderN2(order.num, stateContext.year, orderTypeRef.current);

                if (orderState.length > 0 && (orderState[0].estado !== order.stateN2 || orderState[0].preparado !== order.preparerN2)) {
                    order.stateN2 = (orderState[0].estado === null ? '1' : orderState[0].estado);
                    order.preparerN2 = (orderState[0].preparado === null ? '0' : orderState[0].preparado);
                } else {
                    update = false;
                }

                order.n2 = true;

                if (update) {
                    updateState({order: order});
                }
            }
        }
    };

    // Conexión al socket, sondeo periódico de nave 2 y limpieza del estado global al
    // salir. Las funciones que se usan aquí quedan capturadas en el primer render, pero
    // leen valor actual a través de estadoRef/orderTypeRef, igual que hacía la clase.
    useEffect(() => {
        const interval = setInterval(() => checkOrderN2(null), 120000);
        const socketClient = new SocketClient();
        socketClient.connect();

        socketClient.receiveUpdateSale(async (data) => {
            if (data[0].pedido === orderRef.current.value) {
                await getSales(updateState, estadoRef.current, true);
            }
        })

        return () => {
            clearInterval(interval); // Limpia el intervalo al desmontar el componente
            handleOrderChange.clear();
            updateState({toasts: []})
            updateState({products:[]});
            updateState({order: new Order()});
            socketClient.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box id="sales" sx={{ width: '100%' }}>
            {/* Estructura estándar de página: Card sin padding propio y un Box por
                sección. El responsive va con sx { xs, md } en lugar de useMediaQuery
                porque aquí solo afecta a estilos, sin estado que derivar */}
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                {/* Header */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            color: "#212529",
                            mb: 0.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            fontSize: { xs: "1.25rem", md: "1.5rem" }
                        }}
                    >
                        <ReceiptLongIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
                        Puntear pedidos
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6c757d" }}>
                        Busca un albarán o pedido para puntear sus productos
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2
                        }}
                    >
                        {/* Select de tipo de orden */}
                        <Box>
                            <FieldLabel icon={CategoryIcon}>Tipo</FieldLabel>
                            <Select
                                id="list-order-type"
                                name="list-order-type"
                                value={orderType}
                                fullWidth
                                onChange={handleChange}
                                sx={{
                                    backgroundColor: "#fff",
                                }}
                            >
                                {orderTypesWithLabelsSpecial.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>

                        {/* Input de número de orden */}
                        <Box>
                            <FieldLabel icon={TagIcon}>Número de {orderType}</FieldLabel>
                            <TextField
                                type="text"
                                id="order"
                                name="order"
                                fullWidth
                                placeholder="Ej: 00-12345"
                                inputRef={orderRef}
                                onFocus={() => {
                                    orderRef.current.value = ''
                                }}
                                onChange={handleOrderChange}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "#fff",
                                    },
                                }}
                            />
                        </Box>

                        {/* Botón de búsqueda */}
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={state.loading}
                            startIcon={state.loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                            sx={{ ...BOTON_PRIMARIO_SX, height: ALTURA_BOTON.FILTRO }}
                        >
                            {state.loading ? 'Buscando...' : 'Buscar'}
                        </Button>
                    </Box>
                </Box>

                {/* Contenido */}
                <Box sx={{ px: { xs: 2, md: 3 }, pb: 3 }}>
                    <ListOrder nave={'1'} type={orderType} handle={ORDER_ACTION.VENTAS} registerAction={SalesService.registerSale} order={orderRef} preparer={true} reviewer={true} camera={true} orderStateN2={false} permitirImagenesCompleto={true} />
                </Box>
            </Card>
        </Box>
    )
};

export default Sales;
